/* ---------------------------------------------------------------------------
 * cart-supply.js — the cart's quantity and supply menus.
 *
 * Kept out of cart-widget.js on purpose: that file is the cart's settled
 * request/re-render machinery and nothing here changes it. Everything below is
 * additive and reaches it only through the globals it already exports —
 * updateCartData, setQuantity, upgradeItem, fetchSection.
 *
 * Delegated from document, because assets/cart-widget.js replaces the whole
 * cart's innerHTML after every change: a handler bound to a pill would be
 * thrown away with the pill. The markup is snippets/new-cart.liquid; each menu
 * row carries what to do in data attributes and this decides nothing about
 * pricing or plans.
 *
 * The menu is position:fixed and placed here rather than absolutely inside the
 * row, because .cart-body scrolls and would otherwise clip it.
 * ------------------------------------------------------------------------- */
(function () {
    if (window.ncMenuReady) return;
    window.ncMenuReady = true;

    var still = window.matchMedia('(prefers-reduced-motion: reduce)');
    var open = null;

    function menuOf(sel) { return sel.querySelector('[data-nc-menu]'); }
    function toggleOf(sel) { return sel.querySelector('[data-nc-toggle]'); }
    function options(sel) {
        return Array.prototype.slice.call(sel.querySelectorAll('[role="option"]'));
    }

    /* Hung off the pill's RIGHT edge: the pills are right-aligned in their row,
       so a menu wider than its button should grow leftwards into the card
       rather than off the drawer. Flips above when there is no room below. */
    function place(sel) {
        var menu = menuOf(sel);
        var button = toggleOf(sel);
        var b = button.getBoundingClientRect();
        var gap = 8;
        var edge = 10;

        sel.removeAttribute('data-flip');
        menu.style.setProperty('--nc-menu-min', Math.round(b.width) + 'px');

        /* The drawer is slid open with a transform, and a transformed ancestor
           becomes the containing block for position:fixed inside it — so 0,0 is
           the drawer's corner, not the viewport's. Rather than special-case
           that, put the menu at zero, see where zero actually landed, and place
           from there: correct with or without a transformed ancestor. */
        menu.style.setProperty('--nc-menu-x', '0px');
        menu.style.setProperty('--nc-menu-y', '0px');
        var m = menu.getBoundingClientRect();

        var top = b.bottom + gap;
        if (top + m.height > window.innerHeight - edge && b.top - gap - m.height > edge) {
            top = b.top - gap - m.height;
            sel.setAttribute('data-flip', '');
        }

        var left = Math.min(Math.max(edge, b.right - m.width),
                            window.innerWidth - m.width - edge);

        menu.style.setProperty('--nc-menu-x', Math.round(left - m.left) + 'px');
        menu.style.setProperty('--nc-menu-y', Math.round(top - m.top) + 'px');
    }

    function onKey(event) {
        if (!open) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
            return;
        }

        var rows = options(open);
        if (!rows.length) return;
        var at = rows.indexOf(document.activeElement);

        /* A listbox walks with the arrows; Tab leaves it, which is what a
           dropdown does and a dialog does not. */
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            var step = event.key === 'ArrowDown' ? 1 : -1;
            var next = at < 0 ? (step > 0 ? 0 : rows.length - 1)
                              : (at + step + rows.length) % rows.length;
            rows[next].focus();
        } else if (event.key === 'Home') {
            event.preventDefault();
            rows[0].focus();
        } else if (event.key === 'End') {
            event.preventDefault();
            rows[rows.length - 1].focus();
        } else if (event.key === 'Tab') {
            close(false);
        }
    }

    /* Fixed to the viewport, so the drawer scrolling under it would leave it
       behind the pill it belongs to. */
    function onReflow() {
        if (!open || !document.contains(open)) { close(false); return; }
        place(open);
    }

    function openMenu(sel) {
        if (open === sel) { close(true); return; }
        if (open) close(false);

        var menu = menuOf(sel);
        var button = toggleOf(sel);
        if (!menu || !button) return;

        open = sel;

        /* Shown before it is measured — hidden, it has no box. */
        menu.hidden = false;
        menu.classList.remove('is-closing');
        clearTimeout(menu.ncTimer);

        place(sel);

        /* Flush the layout before the class lands, or the animation has no
           start state to run from. place() already reads a rect, so this is
           belt and braces — but it is synchronous, which a rAF is not, and the
           class must be on before anything else can look at it. */
        void menu.offsetWidth;
        menu.classList.add('is-open');

        button.setAttribute('aria-expanded', 'true');

        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);

        var on = sel.querySelector('[role="option"].is-on') || options(sel)[0];
        if (on) on.focus({ preventScroll: true });
    }

    function close(returnFocus) {
        if (!open) return;

        var sel = open;
        open = null;

        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onReflow);
        window.removeEventListener('scroll', onReflow, true);

        /* The cart may already have re-rendered this node away. */
        if (!document.contains(sel)) return;

        var menu = menuOf(sel);
        var button = toggleOf(sel);
        if (button) button.setAttribute('aria-expanded', 'false');
        if (!menu) return;

        menu.classList.remove('is-open');
        menu.classList.add('is-closing');

        clearTimeout(menu.ncTimer);
        menu.ncTimer = setTimeout(function () {
            menu.classList.remove('is-closing');
            menu.hidden = true;
        }, still.matches ? 120 : 170);

        if (returnFocus && button && sel.contains(document.activeElement)) {
            button.focus({ preventScroll: true });
        }
    }

    /* ---------------------------------------------------------------------
       Choosing a row
       --------------------------------------------------------------------- */

    function choose(opt) {
        if (opt.classList.contains('is-on')) { close(true); return; }

        var key = opt.getAttribute('data-key');
        var qty = parseInt(opt.getAttribute('data-qty'), 10) || 1;
        var plan = parseInt(opt.getAttribute('data-plan'), 10) || null;
        var variant = parseInt(opt.getAttribute('data-variant'), 10) || null;

        var act = opt.getAttribute('data-nc-act');

        /* The cart's three actions all fire a request and the row waits out the
           re-render. The sticky bar's is a choice about something not yet in
           the cart, so there is nothing to wait for and nothing to grey out. */
        if (act !== 'v8sub') opt.classList.add('is-busy');

        switch (act) {
        case 'qty':
            setQuantity(key, opt.getAttribute('data-value'));
            break;
        case 'plan':
            upgradeItem(key, qty, plan);
            break;
        case 'supply':
            setSupply(key, variant, plan, qty);
            break;
        case 'v8sub':
            /* Defined by snippets/v8-cart.liquid, which owns what the bar's
               Add to Cart adds. Absent — any page without that bar — the row
               simply does nothing rather than throwing. */
            if (typeof window.v8ChooseSupply === 'function') window.v8ChooseSupply(opt);
            break;
        }

        /* The row gets a moment of its own before the re-render takes it. */
        setTimeout(function () { close(false); }, still.matches ? 90 : 260);
    }

    document.addEventListener('click', function (event) {
        var opt = event.target.closest('[role="option"][data-nc-act]');
        if (opt) {
            event.preventDefault();
            choose(opt);
            return;
        }

        var button = event.target.closest('[data-nc-toggle]');
        if (button) {
            event.preventDefault();
            openMenu(button.closest('[data-nc-sel]'));
            return;
        }

        /* Anywhere else puts it away. No catcher element: a dropdown that
           blocks the page has become a dialog. */
        if (open && !event.target.closest('[data-nc-menu]')) close(false);
    });
}());

/* -----------------------------------------------------------------------------
 * setSupply — move a line to a different pack AND cadence in one go.
 *
 * The 30-day supply is the 1-bottle variant on its 30-day plan and the 90-day
 * supply is the 3-bottle variant on its 90-day plan; neither variant carries
 * the other's plan. /cart/change.js can change a line's quantity, properties
 * and selling plan but NOT its variant, so the line is dropped and re-added.
 * Only the add asks for the section, so the cart re-renders once.
 * -------------------------------------------------------------------------- */
async function setSupply(key, variantId, planId, quantity) {
    var row = document.getElementById(key);
    if (row) row.classList.add('active');

    var section = (typeof fetchSection === 'undefined') ? 'cart-section' : fetchSection;

    try {
        var dropped = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!dropped.ok) throw new Error('cart/change responded ' + dropped.status);

        var line = { id: variantId, quantity: quantity };
        if (planId) line.selling_plan = planId;
        var pdp = row && row.getAttribute('data-nc-pdp');
        if (pdp) line.properties = { _pdp: pdp };

        var added = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [line], sections: section })
        });
        if (!added.ok) throw new Error('cart/add responded ' + added.status);

        var body = await added.json();

        /* Carried over from the rest of the cart: wholesale carts reload. */
        if (typeof isWholeSale !== 'undefined') {
            location.reload();
            return;
        }

        updateCartData(body);
    } catch (error) {
        console.log(error);
        if (row) row.classList.remove('active');
        alert('An error occurred while processing your request. Please try again later.');
    }
}